import { RefObject, useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"

export const useSavedFunction = <T,>(defaultValue:T):[T, (newValue:T)=>void]=>{
        const [value, reactSetter] = useState<T>(() => defaultValue)

    const setter = (newFilter:T)=>{
        reactSetter(()=>newFilter)
    }
    return [value, setter]
}

export const useUrlParam = (name:string):[string|undefined, (v:string|undefined)=>void]=>{
    const [params, setParam] = useUrlParams()

    // console.log("params", params)
    const value = params[name]
    // console.log(name, value, "params", params)
    return [value, (v)=>setParam(name, v)]
}

export const useUrlParamWithDefault = (name:string, defaultValue:string):[string, (v:string|undefined)=>void]=>{
    const [value, setValue] = useUrlParam(name)
    
    return [value ?? defaultValue, setValue]
}


export const useJsonUrlParam = <T,>(name:string, defaultValue:T):[T, (v:T)=>void]=>{
    const [params, setParam] = useUrlParams()
    const jsonValue = params[name]
//     console.log(`"${name}" is '${jsonValue}'`)
    const value:T = jsonValue !== undefined ? JSON.parse(jsonValue) : defaultValue
    return [value, (v)=> setParam(name, (v!== undefined) ? JSON.stringify(v) : undefined)]
}

export const toQueryString = (params: Record<string, any>): string => Object.keys(params).flatMap(key => {
    const value = params[key]
    return value ? [`${key}=${value ?? ''}`] : []
}).join("&")

export const useUrlParams = ():[Record<string, string>, (name:string, value:string|undefined)=>void]=>{

    let [params, setParams] = useSearchParams();

    const setParam = (name:string, value:string|undefined)=>{
       setParams((prev) => {
        if(value === undefined){
            prev.delete(name);
        }else{
            prev.set(name, value);
        }
        return prev;
      })
    }


    return [readUrlParams(params), setParam]
}


export const readUrlParams = (p:URLSearchParams):Record<string, string> => {
    const params:Record<string, string> = {}

    const e = p.entries()
    // console.log("e is ", e)
    let keepGoing = true
    while(keepGoing){
        const n = e.next()
        if(n.done){
            keepGoing = false
        }else{
            // console.log("n is ", n)
            const [key, value] = n.value
            params[key] = value
        }
    }

    return params
}

export const useDivSize = ():[{width:number, height:number} | undefined, RefObject<HTMLDivElement | null>]=>{
  const ref = useRef<HTMLDivElement>(null)
  const [value, setValue] = useState<{width:number, height:number}>()
  
  useEffect(()=>{
    if(ref.current){
      setValue({
        height:ref.current.offsetHeight,
        width:ref.current.offsetWidth,
      })
    }
  }, [ref.current?.offsetHeight])

  return [value, ref]
}


export const usePromise = <T,>(fn:()=>Promise<T>):T|undefined =>{
    const [instance, setInstance] = useState<T>()

    useEffect(()=>{
        fn().then(setInstance)
    }, [])

    return instance
}


export const useProgressPromise = <T,>(fn:()=>Promise<T>):{working:boolean, result:T|undefined, error:string|undefined, reload:()=>Promise<any>}=>{
    const [working, setWorking] = useState(true)
    const [result, setResult] = useState<T>()
    const [error, setError] = useState<string>()
    
    const reload = ()=>{
        setWorking(true)
        return fn()
            .then(r=>{
                setError(undefined)
                setResult(r)
            })
            .catch(e=>setError(e.toString()))
            .finally(()=>{
                setWorking(false)
            })
    };
    useEffect(()=>{reload()}, [])

    return  {working, result, error, reload}
}

export interface PromiseHook<T, E> {
    working:boolean, 
    result:T|undefined, 
    error:E|undefined, 
    reload:()=>Promise<any>
}
export const useProgressPromiseError = <T,E>(fn:()=>Promise<T>):PromiseHook<T, E>=>{
    const [working, setWorking] = useState(true)
    const [result, setResult] = useState<T>()
    const [error, setError] = useState<E>()
    
    const reload = ()=>{
        setWorking(true)
        return fn()
            .then(r=>{
                setError(undefined)
                setResult(r)
            })
            .catch(e=>setError(e))
            .finally(()=>{
                setWorking(false)
            })
    };
    useEffect(()=>{reload()}, [])

    return  {working, result, error, reload}
}

export const useReloadablePromise = <T,>(fn:()=>Promise<T>):{value:T|undefined, isLoading:boolean, reload:()=>void} =>{
    const [instance, setInstance] = useState<T>()
    const [loading, setLoading] = useState(true)

    const reload = ()=> {
        setLoading(true)
        fn()
            .then(setInstance)
            .finally(()=>setLoading(false))
    }

    useEffect(()=>{
        reload()
    }, [])

    return {value:instance, isLoading:loading, reload}
}

